/**
 * safeStorage.js
 *
 * iOS Safari Private Browsing mode throws QuotaExceededError on ANY
 * localStorage / sessionStorage access (even .getItem). Unguarded calls
 * crash the module at import time, producing a white screen on iPhone
 * when users arrive from Google results (Private mode is common there).
 *
 * Use these wrappers everywhere instead of window.(local|session)Storage.
 * They never throw; errors are swallowed and getItem returns null on
 * failure, setItem/removeItem no-op silently.
 *
 * DO NOT add error logging here — the whole point is to fail invisibly
 * in constrained browser environments. If you need telemetry, do it at
 * the call site with your own try/catch.
 */

function _safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch (_e) {
    return null;
  }
}

function _safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
    return true;
  } catch (_e) {
    return false;
  }
}

function _safeRemove(storage, key) {
  try {
    storage.removeItem(key);
    return true;
  } catch (_e) {
    return false;
  }
}

function _getBackend(kind) {
  try {
    if (typeof window === "undefined") return null;
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch (_e) {
    return null;
  }
}

export const safeLocalStorage = {
  getItem(key) {
    const s = _getBackend("local");
    return s ? _safeGet(s, key) : null;
  },
  setItem(key, value) {
    const s = _getBackend("local");
    return s ? _safeSet(s, key, value) : false;
  },
  removeItem(key) {
    const s = _getBackend("local");
    return s ? _safeRemove(s, key) : false;
  },
};

export const safeSessionStorage = {
  getItem(key) {
    const s = _getBackend("session");
    return s ? _safeGet(s, key) : null;
  },
  setItem(key, value) {
    const s = _getBackend("session");
    return s ? _safeSet(s, key, value) : false;
  },
  removeItem(key) {
    const s = _getBackend("session");
    return s ? _safeRemove(s, key) : false;
  },
};

/** Returns true if we can actually persist. Useful for UI decisions
 * (e.g. "don't prompt the user to remember me if we can't store it"). */
export function isStorageAvailable(kind = "local") {
  const s = _getBackend(kind);
  if (!s) return false;
  const probe = "__gively_probe__";
  try {
    s.setItem(probe, "1");
    s.removeItem(probe);
    return true;
  } catch (_e) {
    return false;
  }
}
